// M4-B §6: rig market 2000-2029 — day rates by class & year (DAY_RATES_M4 continues the M3 curves), rig counts and
// floater utilization (charter-offer counts and bargaining), a land-rig contract board (offers ∝ US rig count / 1,900),
// cold-stack reactivation (> 2 years in storage: 45 days and 15% of the new price), and the frac-service index the shale
// workstream (A) prices completions with. Hooks: m3/opsutil.charterRate and m3/charters.refreshCharterBoard (M4-B).
import {
  DAY_RATES_M4, dayRateM4, usRigCount, US_RIG_COUNT, FRAC_SPREAD_COUNT, fracUtilization, fracRateIndex, fracWaitDays,
  FLOATER_UTILIZATION, lerpAt, M4_FROM,
} from '../../data/m4/rules.js';
import { eqDef, fieldDef, stat, unitPrice, yf } from '../defs.js';
import { fieldOpen } from '../survey.js';
import { activeRigCrew } from '../crew.js';
import { spend, fmt$, nextId } from '../util.js';
import { mk, peek, m4On, rand, isOffshoreField, r2 } from './marketCore.js';

export const LAND_BOARD_EVERY_DAYS = 30;
export const LAND_BOARD_MAX = 6;
export const LAND_RIG_COUNT_FULL = 1900;    // M4_SPEC §6: offer count ∝ usRigCount(y) / 1,900
export const COLD_STACK_DAYS = 730;         // > 2 years in storage → reactivation project
export const REACTIVATION_DAYS = 45;
export const REACTIVATION_COST_FRAC = 0.15;
const CLASS_REMAP = { drillship: 'drillship_ultra' }; // M3 class names → the M4 curve that continues them
const LAND_CLASSES = ['land', 'land_deep', 'land_ac', 'super_spec'];
const LAND_RANK = { land: 0, land_deep: 1, land_ac: 2, super_spec: 3 };
const CLASS_FROM = { land_ac: 2002, super_spec: 2010, semisub_harsh: 2012 };

// Day rate ($/day nominal) for a class from 2000, or null (callers fall back to the M3 curve).
export function m4DayRate(cls, y) {
  if (y < M4_FROM) return null;
  const c = CLASS_REMAP[cls] ?? cls;
  return DAY_RATES_M4[c] ? dayRateM4(c, y) : null;
}
export const landUtilization = (y) => Math.max(0, Math.min(1, usRigCount(y) / LAND_RIG_COUNT_FULL));
export const floaterUtilization = (y) => lerpAt(FLOATER_UTILIZATION, y);
// Utilization a rig def bargains against from 2000 (null before).
export function m4UtilizationFor(def, y) {
  if (y < M4_FROM) return null;
  return stat(def, 'offshore', false) ? floaterUtilization(y) : landUtilization(y);
}
// M3 charter-out board size from 2000 (offshore: floater utilization).
export const m4CharterOfferCount = (y) => (y < M4_FROM ? null : Math.round(6 * Math.max(0, Math.min(1, (floaterUtilization(y) - 0.4) / 0.58))));
export const landOfferCount = (y) => Math.round(LAND_BOARD_MAX * landUtilization(y));

// ---- for workstream A: completion-service tightness -------------------------------------------------------------
export const fracRateIndexAt = (state) => (yf(state) < M4_FROM ? 1 : fracRateIndex(yf(state)));
export const serviceWaitDays = (state) => (yf(state) < M4_FROM ? 0 : fracWaitDays(yf(state)));

// ---- land contract board ---------------------------------------------------------------------------------------
const rigClass = (def) => stat(def, 'dayRateClass', stat(def, 'offshore', false) ? 'jackup' : 'land');
function onshoreOpenFields(state) {
  return Object.keys(state.fields).filter((fid) => { const d = fieldDef(fid); return d && (d.body ?? 'earth') === 'earth' && !isOffshoreField(fid) && fieldOpen(state, fid); });
}
function idleLandRigs(state) {
  return Object.values(state.rigs).filter((r) => !r.wellId && !r.charterOut && !r.stacked && !r.mothballed && !stat(eqDef(r.defId), 'offshore', false));
}

export function refreshLandBoard(state) {
  const M = mk(state);
  const y = yf(state);
  const fids = onshoreOpenFields(state);
  const classes = LAND_CLASSES.filter((c) => y >= (CLASS_FROM[c] ?? 0));
  const n = fids.length ? landOfferCount(y) : 0;
  const board = [];
  for (let i = 0; i < n; i++) {
    const fid = fids[Math.floor(rand(state, `lb:f${i}`) * fids.length)];
    const cls = classes[Math.floor(rand(state, `lb:c${i}`) * classes.length)];
    const days = 60 + Math.floor(rand(state, `lb:d${i}`) * 306);
    const rate = Math.round(dayRateM4(cls, y) * (0.85 + 0.3 * rand(state, `lb:r${i}`)) / 100) * 100;
    board.push({ id: nextId(state, 'lc'), fieldId: fid, rigClass: cls, days, rate, operator: fieldDef(fid)?.serviceOnly?.operator ?? 'an independent operator', postedDay: state.day, expiresDay: state.day + LAND_BOARD_EVERY_DAYS });
  }
  M.landBoard = board;
  M.landBoardDay = state.day;
  return board;
}

export function tickLandBoard(state) {
  const M = peek(state);
  if (state.day - (M?.landBoardDay ?? -Infinity) < LAND_BOARD_EVERY_DAYS) return;
  if (!idleLandRigs(state).length && !(M?.landBoard?.length)) return;
  refreshLandBoard(state);
}

export function acceptLandContractImpl(state, offerId, rigUid) {
  const M = peek(state);
  const o = (M?.landBoard ?? []).find((x) => x.id === offerId);
  if (!o) return { ok: false, msg: 'That contract has been taken.' };
  const rig = state.rigs[rigUid];
  if (!rig) return { ok: false, msg: 'No such rig.' };
  const def = eqDef(rig.defId);
  if (stat(def, 'offshore', false)) return { ok: false, msg: `${rig.name} is an offshore rig.` };
  if (rig.wellId || rig.charterOut || rig.stacked || rig.mothballed || (rig.readyDay && rig.readyDay > state.day)) return { ok: false, msg: `${rig.name} is busy.` };
  const have = LAND_RANK[rigClass(def)] ?? 0, need = LAND_RANK[o.rigClass] ?? 0;
  if (have < need) return { ok: false, msg: `The operator wants a ${o.rigClass.replace('_', '-')} rig; ${rig.name} doesn't qualify.` };
  if (!activeRigCrew(state, rig).some((c) => c.role === 'driller')) return { ok: false, msg: `${rig.name} has no driller.` };
  rig.charterOut = { offerId, fieldId: o.fieldId, rate: o.rate, startDay: state.day, untilDay: state.day + o.days, operator: o.operator, earned: 0, land: true };
  M.landBoard = M.landBoard.filter((x) => x !== o);
  return { ok: true, msg: `${rig.name} contracted to ${o.operator} at ${fieldDef(o.fieldId)?.name}: ${fmt$(o.rate)}/day for ${o.days} days.` };
}

// ---- cold stack (M2 mothball) reactivation, hook in sim/austerity.reactivateImpl ------------------------------------
export function m4ReactivateGuard(state, rig) {
  if (!m4On(state) || !rig?.mothballed) return null;
  const since = rig.mothballed.sinceDay ?? state.day;
  if (state.day - since <= COLD_STACK_DAYS) return null;
  const cost = Math.round(unitPrice(state, eqDef(rig.defId)) * REACTIVATION_COST_FRAC);
  if (!spend(state, cost, 'capex')) return { ok: false, msg: `${rig.name} has been cold-stacked for ${Math.round((state.day - since) / 365 * 10) / 10} years: reactivation costs ${fmt$(cost)} (15% of a new rig) and takes ${REACTIVATION_DAYS} days.` };
  rig.mothballed = null;
  rig.readyDay = state.day + REACTIVATION_DAYS;
  (mk(state).reactivating)[rig.uid] = { untilDay: rig.readyDay, cost };
  return { ok: true, msg: `${rig.name} comes out of cold stack: new seals, recertified BOP and pumps, a crew to train — ${fmt$(cost)} and ${REACTIVATION_DAYS} days.` };
}
export function reactivationQuote(state, rig) {
  if (!rig?.mothballed) return null;
  const since = rig.mothballed.sinceDay ?? state.day;
  const cold = m4On(state) && state.day - since > COLD_STACK_DAYS;
  return { rigUid: rig.uid, stackedDays: state.day - since, cold, days: cold ? REACTIVATION_DAYS : 5, cost: cold ? Math.round(unitPrice(state, eqDef(rig.defId)) * REACTIVATION_COST_FRAC) : 0 };
}

// ---- view ----------------------------------------------------------------------------------------------------
const CLASS_NAMES = { land: 'Land (mechanical/SCR)', land_deep: 'Deep land', land_ac: 'AC-drive land', super_spec: 'Super-spec pad', barge: 'Inland barge', jackup: 'Jackup',
  jackup_harsh: 'Harsh-environment jackup', semisub: 'Semisubmersible', semisub_deep: 'Deepwater semi', semisub_harsh: 'Harsh-environment semi', drillship_ultra: 'Ultra-deepwater drillship', cushing_storage: 'Cushing tank lease (per MMbbl)' };
export function rigMarketViewImpl(state) {
  const y = yf(state);
  const on = y >= M4_FROM;
  const classes = Object.keys(DAY_RATES_M4).filter((c) => y >= (CLASS_FROM[c] ?? 0)).map((c) => ({
    id: c, name: CLASS_NAMES[c] ?? c, rate: on ? Math.round(dayRateM4(c, y)) : null, yearAgo: on ? Math.round(dayRateM4(c, y - 1)) : null,
    trend: on ? r2(dayRateM4(c, y) / Math.max(1, dayRateM4(c, y - 1)) - 1) : 0, history: DAY_RATES_M4[c].map(([yy, v]) => ({ year: yy, rate: v })),
  }));
  const M = peek(state);
  return {
    year: y, active: on, usRigCount: on ? Math.round(usRigCount(y)) : null, rigCountHistory: US_RIG_COUNT.map(([yy, v]) => ({ year: yy, rigs: v })),
    fracSpreads: on ? Math.round(lerpAt(FRAC_SPREAD_COUNT, y)) : null, fracUtilization: on ? r2(fracUtilization(y)) : null,
    fracRateIndex: r2(fracRateIndexAt(state)), fracWaitDays: serviceWaitDays(state),
    floaterUtilization: on ? r2(floaterUtilization(y)) : null, landUtilization: on ? r2(landUtilization(y)) : null,
    classes, landBoard: (M?.landBoard ?? []).map((o) => ({ ...o, fieldName: fieldDef(o.fieldId)?.name, value: o.rate * o.days,
      fits: idleLandRigs(state).filter((r) => (LAND_RANK[rigClass(eqDef(r.defId))] ?? 0) >= (LAND_RANK[o.rigClass] ?? 0)).map((r) => r.uid) })),
    coldStacked: Object.values(state.rigs).filter((r) => r.mothballed).map((r) => ({ name: r.name, ...reactivationQuote(state, r) })),
    reactivating: Object.entries(M?.reactivating ?? {}).filter(([, v]) => v.untilDay > state.day).map(([uid, v]) => ({ rigUid: uid, ...v, daysLeft: v.untilDay - state.day })),
  };
}
